import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Funnel, Users, ArrowRight, Phone, EnvelopeSimple, TrendUp } from '@phosphor-icons/react';

export default function CRMPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const { data } = await api.get('/crm/leads', { params });
      setLeads(data);
    } catch (error) {
      toast.error('Error al cargar leads');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchPipeline = useCallback(async () => {
    try {
      const { data } = await api.get('/crm/pipeline');
      setPipeline(data);
    } catch (error) {
      console.error('Error loading pipeline', error);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchPipeline();
  }, [fetchLeads, fetchPipeline]);

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500/20 text-blue-400',
      contacted: 'bg-purple-500/20 text-purple-400',
      qualified: 'bg-yellow-500/20 text-yellow-400',
      proposal: 'bg-orange-500/20 text-orange-400',
      negotiation: 'bg-pink-500/20 text-pink-400',
      won: 'bg-emerald-500/20 text-emerald-400',
      lost: 'bg-red-500/20 text-red-400',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const CreateLeadModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      company: '',
      email: '',
      phone: '',
      source: 'web',
      estimated_value: 0,
      notes: ''
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await api.post('/crm/leads', formData);
        toast.success('Lead creado exitosamente');
        setShowCreateModal(false);
        fetchLeads();
      } catch (error) {
        toast.error('Error al crear lead');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-neutral-900 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <h3 className="text-xl font-bold text-white mb-4">Nuevo Lead</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Nombre *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Empresa</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Fuente</label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({...formData, source: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="web">Web</option>
                  <option value="referral">Referido</option>
                  <option value="cold_call">Llamada en frío</option>
                  <option value="event">Evento</option>
                  <option value="social_media">Redes sociales</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Valor Estimado</label>
                <input
                  type="number"
                  value={formData.estimated_value}
                  onChange={(e) => setFormData({...formData, estimated_value: parseFloat(e.target.value)})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Notas</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows="3"
                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Crear Lead
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <Layout title="CRM / Pipeline">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">CRM / Pipeline</h1>
            <p className="text-gray-400 mt-1">Gestión de leads y oportunidades</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
          >
            <Plus size={20} />
            Nuevo Lead
          </button>
        </div>

        {/* Pipeline Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'].map((status) => {
            const stage = pipeline.find(p => p.status === status) || { count: 0, total_value: 0 };
            return (
              <div key={status} className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(status)}`}>
                    {status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="text-2xl font-bold text-white">{stage.count}</div>
                <div className="text-sm text-gray-400">${stage.total_value.toLocaleString()}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex gap-2 items-center">
          <Funnel size={20} className="text-gray-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
          >
            <option value="all">Todos</option>
            <option value="new">Nuevos</option>
            <option value="contacted">Contactados</option>
            <option value="qualified">Calificados</option>
            <option value="proposal">En Propuesta</option>
            <option value="negotiation">En Negociación</option>
            <option value="won">Ganados</option>
            <option value="lost">Perdidos</option>
          </select>
        </div>

        {/* Leads List */}
        <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-800/50">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Nombre</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Empresa</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Contacto</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Fuente</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Estado</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm">Valor Est.</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-400">
                      Cargando...
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-400">
                      No hay leads. Crea el primero.
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead._id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                      <td className="px-4 py-3 text-white font-medium">{lead.name}</td>
                      <td className="px-4 py-3 text-gray-300">{lead.company || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {lead.email && (
                            <div className="flex items-center gap-1 text-sm text-gray-400">
                              <EnvelopeSimple size={14} />
                              {lead.email}
                            </div>
                          )}
                          {lead.phone && (
                            <div className="flex items-center gap-1 text-sm text-gray-400">
                              <Phone size={14} />
                              {lead.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-sm capitalize">
                        {lead.source?.replace('_', ' ')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(lead.status)}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        ${lead.estimated_value?.toLocaleString() || 0}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/crm/${lead._id}`)}
                          className="text-emerald-400 hover:text-emerald-300"
                        >
                          <ArrowRight size={20} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCreateModal && <CreateLeadModal />}
    </Layout>
  );
}
